import { Flex, View } from "@adobe/react-spectrum";
import { Paperclip } from "react-bootstrap-icons";
import moment from "moment";
import {
  truncateText,
  getSenderName,
  isOutgoingMessageForIcon,
} from "../../../../../utils/helperUtil";
import {
  DocumentIcon,
  InvoiceIcon,
  MailInCommingIcon,
  MailOutCommingIcon,
  MassmailGreen,
  NoticeIcon,
  PhoneInComingIcon,
  PhoneInOutGoingIcon,
  PhoneMissedIcon,
  TaskIcon,
} from "../../../../../components/protected/Icons/IconsLib";
import styles from "./card_component.module.css";
import {
  BulkSelectCheckboxSlot,
  useBulkSelectionContext,
} from "../../../../../components/protected/layout-components/BulkSelectCheckbox";

interface ICardComponentProps {
  item: any;
  showUnresolvedReferenceIndicator?: boolean;
}

const CardComponent: React.FC<ICardComponentProps> = ({
  item,
  showUnresolvedReferenceIndicator = false,
}) => {
  const hasBulkSelection = useBulkSelectionContext() != null;
  const rowId = String(item?.id ?? "");
  const showFollowReferenceBadge =
    showUnresolvedReferenceIndicator &&
    String(item?.followReference ?? "") === "0";
  const extractValues = (data: any, prop: string): string[] => {
    if (Array.isArray(data)) {
      return data
        .map((entry) => entry?.[prop])
        .filter(Boolean)
        .map((value) => String(value).trim());
    }
    if (data && typeof data === "object" && data[prop]) {
      return [String(data[prop]).trim()];
    }
    return [];
  };

  const extractFirstAvailable = (data: any, props: string[]): string[] => {
    for (const p of props) {
      const values = extractValues(data, p);
      if (values.length > 0) return values;
    }
    return [];
  };

  const toRecipients = extractValues(item?.RECIPIENT?.TO, "CONTENT");
  const ccRecipients = extractValues(item?.RECIPIENT?.CC, "CONTENT");
  const bccRecipients = extractValues(item?.RECIPIENT?.BCC, "CONTENT");
  const notifyRecipients = extractFirstAvailable(item?.RECIPIENT?.USER, [
    "contact",
    "contactName",
    "CONTENT",
    "userName",
    "name",
    "id",
  ]);

  const primaryRecipient =
    toRecipients[0] ||
    ccRecipients[0] ||
    bccRecipients[0] ||
    notifyRecipients[0] ||
    "";

  const secondaryRecipientLine = [
    ...ccRecipients,
    ...bccRecipients,
    ...notifyRecipients,
    ...toRecipients.slice(1),
  ]
    .filter(Boolean)
    .join(", ");

  const emailTypeImg = () => {
    const renderIcon = (icon: React.ReactNode) => (
      <View UNSAFE_className={styles.email_type_img}>
        {icon}
        {showFollowReferenceBadge && (
          <span className={styles.follow_reference_badge}>?</span>
        )}
      </View>
    );

    switch (item.type) {
      case "1":
      case "12":
        return renderIcon(<NoticeIcon size={24} />);
      case "2":
      case "3":
      case "7":
        if (isOutgoingMessageForIcon(item)) {
          return renderIcon(<MailOutCommingIcon size={24} />);
        } else {
          return renderIcon(<MailInCommingIcon size={24} />);
        }
      case "9":
      case "14":
      case "15":
      case "11":
        return renderIcon(<MailOutCommingIcon size={24} />);
      case "4":
      case "5":
        return renderIcon(<DocumentIcon size={24} />);
      case "6":
        if (item.status === "131072") {
          return renderIcon(<PhoneInComingIcon size={24} height={16} />);
        } else if (item.status === "262144") {
          return renderIcon(<PhoneInOutGoingIcon size={24} height={16} />);
        } else {
          return renderIcon(<PhoneMissedIcon size={24} height={16} />);
        }
      case "8":
        return renderIcon(<MassmailGreen size={24} />);
      case "10":
        return renderIcon(<TaskIcon size={24} />);
      case "13":
        return renderIcon(<InvoiceIcon size={24} />);
      default:
        return null;
    }
  };

  return (
    <View width={"100%"} position={"relative"} height={"81px"}>
      <Flex
        direction={"row"}
        position={"absolute"}
        top={"5px"}
        left={"50px"}
        alignItems={"center"}
        justifyContent={"start"}
        gap={"8px"}
        UNSAFE_style={{ width: "calc(100% - 170px)" }}
        UNSAFE_className={styles.user_detail_parent}
      >
        <View
          position={"relative"}
          UNSAFE_className={
            item?.seen == "1" ? styles.user_name_read : styles.user_name_unread
          }
        >
          <span title={item?.sender}>
            {truncateText(getSenderName(item?.sender), 14)}
          </span>
        </View>
        {primaryRecipient && (
          <View
            width={"1px"}
            position={"relative"}
            height={"12px"}
            UNSAFE_className={styles.user_detail_parent_line}
          ></View>
        )}
        <View position={"relative"} UNSAFE_className={styles.user_email}>
          <span title={primaryRecipient}>
            {truncateText(primaryRecipient, 36)}
          </span>
        </View>
      </Flex>
      <View
        position={"absolute"}
        top={"27px"}
        left={"50px"}
        UNSAFE_style={{ width: "calc(100% - 170px)" }}
        UNSAFE_className={
          item?.seen == "1"
            ? styles.email_subject_read
            : styles.email_subject_unread
        }
      >
        <span title={item?.subject}>{truncateText(item?.subject, 46)}</span>
      </View>
      <View
        position={"absolute"}
        top={"50px"}
        left={"50px"}
        UNSAFE_style={{ width: "calc(100% - 170px)" }}
        UNSAFE_className={styles.user_email_other_options}
      >
        <span title={secondaryRecipientLine}>
          {truncateText(secondaryRecipientLine, 42)}
        </span>
      </View>
      <View
        position={"absolute"}
        top={"50px"}
        right={"10px"}
        width={"100px"}
        UNSAFE_className={styles.email_date}
      >
        {moment(item?.date).format("DD.MM.YYYY HH:mm")}
      </View>
      <View
        position={"absolute"}
        top={"10px"}
        right={hasBulkSelection ? "38px" : "10px"}
        width={"16px"}
        UNSAFE_className={styles.attachment_icon}
      >
        {item?.numberOfAttachments > 0 && <Paperclip size={12} />}
      </View>
      {emailTypeImg()}
      <View
        position={"absolute"}
        top={"6px"}
        right={"10px"}
        UNSAFE_style={{ zIndex: 2 }}
      >
        <BulkSelectCheckboxSlot rowId={rowId} />
      </View>
    </View>
  );
};

export default CardComponent;
